/// <reference types="@types/googlemaps" />
import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

const AUTOCOMPLETE_CONTROL_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => GoogleAutocompleteComponent),
  multi: true,
};

@Component({
  selector: 'app-google-autocomplete',
  providers: [AUTOCOMPLETE_CONTROL_ACCESSOR],
  templateUrl: './google-autocomplete.component.html',
  styleUrls: ['./google-autocomplete.component.scss'],
})
export class GoogleAutocompleteComponent implements OnInit, AfterViewInit, OnChanges, ControlValueAccessor {
  @Input() addressType: string;
  @Input() defaultValue: string;
  @Input() alteredAddress: string;
  @Output() setAddress: EventEmitter<any> = new EventEmitter();
  @ViewChild('addressText') addressText: any;
  addressWasAltered: boolean;
  public autocompleteInput: UntypedFormControl;
  public model: any;
  public onChange = (value: any) => {};
  public onTouched = () => {};

  ngOnInit() {
    this.autocompleteInput = new UntypedFormControl(this.defaultValue);
    this.autocompleteInput.valueChanges.subscribe((value) => {
      this.model = value;
      this.onTouched();

      this.onChange(this.model);
    });
  }

  ngOnChanges(dataChanges) {
    if ('alteredAddress' in dataChanges && dataChanges.alteredAddress.currentValue) {
      this.autocompleteInput.patchValue(dataChanges.alteredAddress.currentValue);
      this.addressWasAltered = true;
    } else {
      this.addressWasAltered = false;
    }
  }

  ngAfterViewInit() {
    this.getPlaceAutocomplete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any) {
    this.model = value;
  }

  onBlur(): void {
    // forcing the user to select the address from suggestions
    if (!this.addressWasAltered) {
      this.autocompleteInput.patchValue('');
    }
  }

  private getPlaceAutocomplete() {
    this.enableEnterKey(this.addressText.nativeElement);
    if (google?.maps?.places) {
      const autocomplete = new google.maps.places.Autocomplete(this.addressText.nativeElement, {
        componentRestrictions: { country: 'BE' },
        types: [this.addressType], // 'establishment' / 'address' / 'geocode'
      });
      google.maps.event.addListener(autocomplete, 'place_changed', () => {
        const place = autocomplete.getPlace();
        this.invokeEvent(place);
      });
    }
  }

  private invokeEvent(place: any) {
    this.model = place;
    this.onTouched();
    this.onChange(this.model);
    this.setAddress.emit(place);
  }

  private enableEnterKey(input) {
    const { addEventListener } = input;
    const addEventListenerWrapper = (type, listener) => {
      if (type === 'keydown') {
        const listenerCopy = listener;
        listener = (event) => {
          const suggestionSelected = document.getElementsByClassName('pac-item-selected').length;
          if (event.key === 'Enter' && !suggestionSelected) {
            const keyboardEvent = {
              key: 'ArrowDown',
              code: 'ArrowDown',
              keyCode: 40,
            };
            const e = new KeyboardEvent('keydown', keyboardEvent);
            listenerCopy.apply(input, [e]);
          }
          listenerCopy.apply(input, [event]);
        };
      }
      addEventListener.apply(input, [type, listener]);
    };
    input.addEventListener = addEventListenerWrapper;
  }
}
